import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import moment from 'moment/moment';
import React, { SetStateAction, useEffect, useState } from 'react';
import { iManageList } from '../../../app/brand/campaign/manage/calendar/dto/IManageCalendar';
import { Campaign } from '../../../model/campaign/Campaign';

interface iCustomCalendarProps {
    setShowModal: React.Dispatch<SetStateAction<boolean>>;
    setShowUpdateModal: React.Dispatch<SetStateAction<boolean>>;
    setSelectedEventId: React.Dispatch<SetStateAction<number>>;
    setNewEvent: React.Dispatch<SetStateAction<any>>;
    selectedCalendarView: string;
    setIsProposalOpened: React.Dispatch<SetStateAction<boolean>>;
    allManages: iManageList[];
    campaign?: Campaign;
}

/* ---------------- COLORS ---------------- */

export const STATUS_COLORS: Record<string, string> = {
    all: '#3b3b3b',
    completed: '#05bbc9',
    feedback_loop: '#ffd2a8',
    published: '#f6b925',
    reported: '#68cb92',
    approved: '#2f86b8',
    proposed: '#97a5a8',
    pending: '#fc9d79',
    confirmed: '#05bbc9',
    scheduled: '#952cb7',
};

export const withAlpha = (hex: string, alpha = 0.15) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const CustomCalendar = ({
    setShowModal,
    allManages,
    setIsProposalOpened,
    campaign,
    setShowUpdateModal,
    setSelectedEventId,
    selectedCalendarView,
    setNewEvent,
}: iCustomCalendarProps) => {
    const [maxDaysEvents, setMaxDaysEvents] = useState(2);
    const [currentCalendarDate, setCurrentCalendarDate] = useState<Date>(new Date());

    const handleEventClick = (info: any) => {
        const eventId = +info.event?._def?.publicId;
        const eventStatus = info?.event?._def?.extendedProps?.status;

        if (eventStatus === 'proposed') {
            setIsProposalOpened(true);
        } else {
            setShowUpdateModal(true);
        }

        setSelectedEventId(eventId);
    };

    const handleDateClick = (event: any) => {
        setCurrentCalendarDate(new Date(event?.date));
        setNewEvent((prev: any) => ({
            ...prev,
            postingDate: `${event?.dateStr}T05:00:00.000Z`,
        }));
        setShowModal(true);
    };

    const handleDatesRender = (event: any) => {
        const { view } = event;
        const { calendarApi } = view.getCurrentData();

        const selectedCalendarDate = new Date(calendarApi.currentDataManager.data.currentDate);
        selectedCalendarDate.setHours(12);

        if (selectedCalendarView === 'dayGridWeek') {
            setCurrentCalendarDate(selectedCalendarDate);
        } else {
            const currentMonth = new Date(currentCalendarDate).getMonth();
            const selectedMonth = selectedCalendarDate.getMonth();

            if (currentMonth !== selectedMonth) {
                setCurrentCalendarDate(new Date(selectedCalendarDate.getFullYear(), selectedMonth, 1));
            }
        }
    };

    const CustomCalendarJSX = (props: any) => {
        return (
            <FullCalendar
                height="auto"
                plugins={[dayGridPlugin, bootstrap5Plugin, listPlugin, interactionPlugin]}
                initialView={props.selectedCalendarView}
                dateClick={handleDateClick}
                eventClick={handleEventClick}
                validRange={{
                    start: campaign?.publishFrom,
                    end: moment(campaign?.publishTo).add(1, 'days').toISOString(),
                }}
                themeSystem="bootstrap5"
                headerToolbar={{
                    left: 'prev today next',
                    center: 'title',
                    right: '',
                }}
                firstDay={1}
                timeZone="local"
                dayMaxEvents={props.maxDaysEvents}
                datesSet={props.handleDatesRender}
                initialDate={props.currentCalendarDate}
                events={allManages as any}
                eventContent={renderEventContent}
            />
        );
    };

    useEffect(() => {
        if (selectedCalendarView === 'dayGridWeek') {
            setMaxDaysEvents(20);
        } else {
            setMaxDaysEvents(3);
        }
    }, [selectedCalendarView]);

    return (
        <CustomCalendarJSX
            selectedCalendarView={selectedCalendarView}
            handleDatesRender={handleDatesRender}
            maxDaysEvents={maxDaysEvents}
            currentCalendarDate={currentCalendarDate}
        />
    );
};

function renderEventContent(eventInfo: any) {
    const iconProps = eventInfo?.event?._def?.extendedProps;
    const eventStatus = iconProps?.status || 'scheduled';
    const statusColor = STATUS_COLORS[eventStatus] || '#198754';
    const backgroundColor = withAlpha(statusColor, 0.15);
    const hasNotifications = !!iconProps?.eventNotifications?.[0]?.isUnread;
    return (
        <div
            className="calendar-event-content"
            style={{
                borderLeftColor: statusColor,
                backgroundColor,
            }}
        >
            <div className="event-info">
                <div className="event-icon">
                    <img
                        src={iconProps?.offerItem?.socialPlatform.path}
                        alt={iconProps?.offerItem?.socialPlatform.name}
                        style={{
                            width: '20px',
                            height: '20px',
                            objectFit: 'contain',
                        }}
                    />
                </div>
                <span className="event-name">{iconProps?.offerItem?.offer?.user?.displayName}</span>
                {hasNotifications && <div className="notification-bullet-red calendar" />}
            </div>
        </div>
    );
}
